import React, { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

// A mobil-onboarding (ClubOnboarding.tsx) értékeivel azonos listák, hogy a séma egyezzen
const CLUB_TYPES = ['Darts klub', 'Egyesület', 'Pub / bar', 'Egyéb'];
const BOARD_TYPES = ['Steel tip', 'Soft tip', 'Mindkettő'];

const EMPTY_CLUB_FORM = {
  name: '',
  shortName: '',
  city: '',
  address: '',
  clubType: CLUB_TYPES[0],
  boardCount: 4,
  boardType: BOARD_TYPES[0],
  email: '',
  phone: '',
  website: '',
  description: ''
};

// A users kollekció tulajdonos-választóhoz szükséges mezői
interface OwnerCandidate {
  uid: string;
  displayName?: string;
  username?: string;
  email?: string;
}

const ownerLabel = (u: OwnerCandidate) => u.displayName || u.username || u.email || u.uid;

interface ClubData {
  id: string; // doksi-id = ownerUid
  ownerUid: string;
  name: string;
  shortName?: string;
  country?: string;
  city?: string;
  address?: string;
  email?: string;
  phone?: string;
  website?: string;
  clubType?: string;
  description?: string;
  boardType?: string;
  boardCount?: number;
  services?: string[];
  verified?: boolean;
  verificationRequested?: boolean;
  verificationRequestedAt?: Timestamp | null;
}

const ClubManagement: React.FC = () => {
  const { isAdmin } = useAuth();
  const [clubs, setClubs] = useState<ClubData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [expandedClubId, setExpandedClubId] = useState<string | null>(null);
  const [bookingCounts, setBookingCounts] = useState<{ [clubId: string]: number | null }>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_CLUB_FORM });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Tulajdonos-választó a modálban
  const [ownerCandidates, setOwnerCandidates] = useState<OwnerCandidate[]>([]);
  const [ownersLoaded, setOwnersLoaded] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<OwnerCandidate | null>(null);

  // A users listát egyszer töltjük be, amikor a modál először kinyílik
  useEffect(() => {
    if (!showCreateModal || ownersLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const snapshot = await getDocs(query(collection(db, 'users'), limit(500)));
        if (cancelled) return;
        const candidates: OwnerCandidate[] = snapshot.docs.map((userDoc) => {
          const data = userDoc.data() as Partial<OwnerCandidate>;
          return {
            uid: userDoc.id,
            displayName: data.displayName,
            username: data.username,
            email: data.email
          };
        });
        setOwnerCandidates(candidates);
        setOwnersLoaded(true);
      } catch (error) {
        console.error('Error loading users for owner picker:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCreateModal, ownersLoaded]);

  // Kliensoldali szűrés: min. 2 karakter, max. 8 találat, kisbetű-érzéketlen
  const ownerQuery = ownerSearch.trim().toLowerCase();
  const ownerMatches =
    ownerQuery.length >= 2
      ? ownerCandidates
          .filter((u) =>
            [u.displayName, u.username, u.email].some(
              (f) => typeof f === 'string' && f.toLowerCase().includes(ownerQuery)
            )
          )
          .slice(0, 8)
      : [];

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'clubs'),
      (snapshot) => {
        const clubsData: ClubData[] = snapshot.docs.map((clubDoc) => ({
          ...(clubDoc.data() as Omit<ClubData, 'id'>),
          id: clubDoc.id
        }));
        // Kliensoldali rendezés név szerint (index-igényes query helyett)
        clubsData.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'hu'));
        setClubs(clubsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading clubs:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const updateCreateForm = (patch: Partial<typeof EMPTY_CLUB_FORM>) =>
    setCreateForm((prev) => ({ ...prev, ...patch }));

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
  };

  const createClub = async () => {
    if (!createForm.name.trim() || !createForm.city.trim()) {
      setCreateError('A klub neve és a város megadása kötelező.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    const ownerUid = selectedOwner?.uid ?? '';
    // Mezőnevek a mobil-onboarding (registerClub) sémájával azonosak
    const clubData = {
      ownerUid,
      name: createForm.name.trim(),
      shortName: createForm.shortName.trim(),
      country: 'Magyarország',
      city: createForm.city.trim(),
      address: createForm.address.trim(),
      email: createForm.email.trim(),
      phone: createForm.phone.trim(),
      website: createForm.website.trim(),
      clubType: createForm.clubType,
      description: createForm.description.trim(),
      boardType: createForm.boardType,
      boardCount: Math.max(1, Math.round(Number(createForm.boardCount) || 1)),
      services: [] as string[],
      verified: true, // admin vette fel, automatikusan hitelesített
      verificationRequested: false,
      createdByAdmin: true,
      createdAt: serverTimestamp()
    };
    try {
      if (ownerUid) {
        // A mobil-flow sémája: clubs/{ownerUid}
        await setDoc(doc(db, 'clubs', ownerUid), clubData);
      } else {
        await addDoc(collection(db, 'clubs'), clubData);
      }
      setCreateForm({ ...EMPTY_CLUB_FORM });
      setSelectedOwner(null);
      setOwnerSearch('');
      setShowCreateModal(false);
      // A lista az onSnapshot miatt magától frissül
    } catch (error) {
      console.error('Error creating club:', error);
      setCreateError('Hiba történt a klub mentésekor. Próbáld újra.');
    } finally {
      setCreating(false);
    }
  };

  const verifyClub = async (clubId: string) => {
    setProcessing(clubId);
    try {
      await updateDoc(doc(db, 'clubs', clubId), {
        verified: true,
        verificationRequested: false
      });
    } catch (error) {
      console.error('Error verifying club:', error);
      alert('Hiba történt a klub hitelesítésekor.');
    } finally {
      setProcessing(null);
    }
  };

  const rejectVerification = async (clubId: string) => {
    setProcessing(clubId);
    try {
      await updateDoc(doc(db, 'clubs', clubId), {
        verificationRequested: false
      });
    } catch (error) {
      console.error('Error rejecting verification:', error);
      alert('Hiba történt a kérelem elutasításakor.');
    } finally {
      setProcessing(null);
    }
  };

  const revokeVerification = async (clubId: string) => {
    if (!window.confirm('Biztosan visszavonod a klub hitelesítését?')) return;
    setProcessing(clubId);
    try {
      await updateDoc(doc(db, 'clubs', clubId), {
        verified: false
      });
    } catch (error) {
      console.error('Error revoking verification:', error);
      alert('Hiba történt a hitelesítés visszavonásakor.');
    } finally {
      setProcessing(null);
    }
  };

  const loadBookingCount = async (clubId: string) => {
    try {
      const bookingsSnapshot = await getDocs(
        query(collection(db, 'bookings'), where('clubId', '==', clubId))
      );
      setBookingCounts((prev) => ({ ...prev, [clubId]: bookingsSnapshot.size }));
    } catch (error) {
      console.error('Error counting bookings:', error);
      setBookingCounts((prev) => ({ ...prev, [clubId]: null }));
    }
  };

  const toggleExpand = (clubId: string) => {
    if (expandedClubId === clubId) {
      setExpandedClubId(null);
      return;
    }
    setExpandedClubId(clubId);
    if (bookingCounts[clubId] === undefined) {
      loadBookingCount(clubId);
    }
  };

  const formatRequestDate = (ts?: Timestamp | null) => {
    if (!ts || typeof ts.toDate !== 'function') return null;
    try {
      return ts.toDate().toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return null;
    }
  };

  const filteredClubs = clubs.filter(
    (club) =>
      club.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      club.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingClubs = filteredClubs.filter((club) => club.verificationRequested && !club.verified);
  const verifiedCount = clubs.filter((club) => club.verified).length;
  const pendingCount = clubs.filter((club) => club.verificationRequested && !club.verified).length;

  const statusBadge = (club: ClubData) => {
    if (club.verified) {
      return <span className="badge-green">Hitelesítve</span>;
    }
    if (club.verificationRequested) {
      return <span className="badge-gold">Függő kérelem</span>;
    }
    return (
      <span className="px-3 py-1 inline-flex text-xs font-semibold rounded-full bg-gray-500/10 text-gray-400 border border-gray-500">
        Nem hitelesített
      </span>
    );
  };

  const contactRow = (club: ClubData) => (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#B8B8C3]">
      {club.email && (
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {club.email}
        </span>
      )}
      {club.phone && (
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          {club.phone}
        </span>
      )}
      {club.website && (
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          {club.website}
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-[#B8B8C3]">Klubok betöltése...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      {/* Statisztika-sor */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stat-card">
          <p className="text-sm text-[#B8B8C3] mb-1">Összes klub</p>
          <p className="text-3xl font-bold text-white">{clubs.length}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-[#B8B8C3] mb-1">Hitelesített</p>
          <p className="text-3xl font-bold text-[#2bc48a]">{verifiedCount}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-[#B8B8C3] mb-1">Függő kérelem</p>
          <p className="text-3xl font-bold text-[#d9a514]">{pendingCount}</p>
        </div>
      </div>

      <div className="card-dark rounded-lg shadow p-6">
        {/* Fejléc */}
        <div className="mb-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-gradient-cyan mb-2 whitespace-nowrap">Klubok</h2>
          <p className="text-[#B8B8C3]">Dart klubok kezelése és hitelesítése</p>
        </div>

        {/* Kereső + új klub gomb */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Keresés név vagy város szerint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-[#010306] text-white placeholder-[#B8B8C3]"
            />
            <svg
              className="absolute left-3 top-3 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          {isAdmin && (
            <button
              onClick={() => {
                setCreateForm({ ...EMPTY_CLUB_FORM });
                setCreateError(null);
                setSelectedOwner(null);
                setOwnerSearch('');
                setShowCreateModal(true);
              }}
              className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto whitespace-nowrap"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Új klub
            </button>
          )}
        </div>

        {/* Ellenőrzési kérelmek */}
        {pendingClubs.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-[#d9a514] mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Ellenőrzési kérelmek ({pendingClubs.length})
            </h3>
            <div className="space-y-3">
              {pendingClubs.map((club) => (
                <div
                  key={`pending-${club.id}`}
                  className="bg-[#d9a514]/5 border border-[#d9a514]/50 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold truncate">{club.name}</p>
                      <span className="badge-gold">Függő kérelem</span>
                    </div>
                    <p className="text-sm text-[#B8B8C3] mt-1">
                      {[club.city, club.address].filter(Boolean).join(', ') || 'Nincs cím megadva'}
                    </p>
                    {formatRequestDate(club.verificationRequestedAt) && (
                      <p className="text-xs text-[#8a8f99] mt-1">
                        Kérelem beküldve: {formatRequestDate(club.verificationRequestedAt)}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => verifyClub(club.id)}
                      disabled={processing === club.id}
                      className="px-4 py-2 bg-[#2bc48a] text-[#010306] rounded-lg hover:bg-[#25a878] transition-all font-semibold text-sm disabled:opacity-50"
                    >
                      Hitelesítés
                    </button>
                    <button
                      onClick={() => rejectVerification(club.id)}
                      disabled={processing === club.id}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold text-sm disabled:opacity-50"
                    >
                      Elutasítás
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Teljes klublista */}
        <h3 className="text-lg font-bold text-white mb-3">Klublista</h3>
        {filteredClubs.length === 0 ? (
          <p className="text-center text-[#B8B8C3] py-8">Nincs megjeleníthető klub.</p>
        ) : (
          <div className="space-y-3">
            {filteredClubs.map((club) => (
              <div key={club.id} className="card-dark-hover p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div
                    className="min-w-0 flex-1 cursor-pointer"
                    onClick={() => toggleExpand(club.id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-white font-semibold">{club.name}</p>
                      {club.shortName && (
                        <span className="text-xs text-[#8a8f99]">({club.shortName})</span>
                      )}
                      {statusBadge(club)}
                    </div>
                    <p className="text-sm text-[#B8B8C3] mb-1">
                      {[club.city, club.address].filter(Boolean).join(', ') || 'Nincs cím megadva'}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#B8B8C3] mb-2">
                      {club.clubType && <span>Típus: {club.clubType}</span>}
                      {(club.boardCount !== undefined || club.boardType) && (
                        <span>
                          Táblák: {club.boardCount ?? '?'}
                          {club.boardType ? ` (${club.boardType})` : ''}
                        </span>
                      )}
                    </div>
                    {contactRow(club)}
                    {club.services && club.services.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {club.services.map((service, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 text-xs rounded-full bg-[#00AFF2]/10 text-[#00AFF2] border border-[#00AFF2]/40"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isAdmin && (
                      club.verified ? (
                        <button
                          onClick={() => revokeVerification(club.id)}
                          disabled={processing === club.id}
                          className="px-3 py-1.5 border border-red-500/50 text-red-400 rounded-lg hover:bg-red-500/10 transition-all font-semibold text-xs disabled:opacity-50"
                        >
                          Visszavonás
                        </button>
                      ) : (
                        <button
                          onClick={() => verifyClub(club.id)}
                          disabled={processing === club.id}
                          className="px-3 py-1.5 bg-[#2bc48a] text-[#010306] rounded-lg hover:bg-[#25a878] transition-all font-semibold text-xs disabled:opacity-50"
                        >
                          Hitelesítés
                        </button>
                      )
                    )}
                    <button
                      onClick={() => toggleExpand(club.id)}
                      className="p-1.5 text-[#B8B8C3] hover:text-white transition-colors"
                      title={expandedClubId === club.id ? 'Bezárás' : 'Részletek'}
                    >
                      <svg
                        className={`w-5 h-5 transition-transform ${expandedClubId === club.id ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Lenyitott részletek */}
                {expandedClubId === club.id && (
                  <div className="mt-4 pt-4 border-t border-[#31383D] grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {club.description && (
                      <div className="sm:col-span-2">
                        <p className="text-[#8a8f99] text-xs uppercase mb-1">Leírás</p>
                        <p className="text-[#B8B8C3]">{club.description}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[#8a8f99] text-xs uppercase mb-1">Ország</p>
                      <p className="text-[#B8B8C3]">{club.country || '–'}</p>
                    </div>
                    <div>
                      <p className="text-[#8a8f99] text-xs uppercase mb-1">Táblatípus</p>
                      <p className="text-[#B8B8C3]">{club.boardType || '–'}</p>
                    </div>
                    <div>
                      <p className="text-[#8a8f99] text-xs uppercase mb-1">Foglalások száma</p>
                      <p className="text-[#B8B8C3]">
                        {bookingCounts[club.id] === undefined
                          ? 'Betöltés...'
                          : bookingCounts[club.id] === null
                            ? 'Nem elérhető'
                            : bookingCounts[club.id]}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#8a8f99] text-xs uppercase mb-1">Tulajdonos (UID)</p>
                      <p className="text-[#B8B8C3] break-all">{club.ownerUid || club.id}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Új klub modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">Új klub felvétele</h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Klub neve <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => updateCreateForm({ name: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Pl. Bulls Eye Darts Club"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Rövid név</label>
                  <input
                    type="text"
                    value={createForm.shortName}
                    onChange={(e) => updateCreateForm({ shortName: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Pl. BEDC"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Város <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.city}
                    onChange={(e) => updateCreateForm({ city: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Pl. Budapest"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Cím</label>
                  <input
                    type="text"
                    value={createForm.address}
                    onChange={(e) => updateCreateForm({ address: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Utca, házszám"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Klubtípus</label>
                  <select
                    value={createForm.clubType}
                    onChange={(e) => updateCreateForm({ clubType: e.target.value })}
                    className="input-dark w-full"
                  >
                    {CLUB_TYPES.map((ct) => (
                      <option key={ct} value={ct}>
                        {ct}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Táblák száma</label>
                  <input
                    type="number"
                    min={1}
                    value={createForm.boardCount}
                    onChange={(e) => updateCreateForm({ boardCount: Number(e.target.value) })}
                    className="input-dark w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Táblatípus</label>
                  <select
                    value={createForm.boardType}
                    onChange={(e) => updateCreateForm({ boardType: e.target.value })}
                    className="input-dark w-full"
                  >
                    {BOARD_TYPES.map((bt) => (
                      <option key={bt} value={bt}>
                        {bt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => updateCreateForm({ email: e.target.value })}
                    className="input-dark w-full"
                    placeholder="klub@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Telefon</label>
                  <input
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => updateCreateForm({ phone: e.target.value })}
                    className="input-dark w-full"
                    placeholder="+36 30 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Weboldal</label>
                  <input
                    type="text"
                    value={createForm.website}
                    onChange={(e) => updateCreateForm({ website: e.target.value })}
                    className="input-dark w-full"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Leírás</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => updateCreateForm({ description: e.target.value })}
                  className="input-dark w-full"
                  rows={3}
                  placeholder="Mutasd be a klubot pár mondatban..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  Tulajdonos (opcionális)
                </label>
                {selectedOwner ? (
                  <div className="inline-flex items-center gap-2 max-w-full bg-[#00AFF2]/10 border border-[#00AFF2]/40 rounded-full pl-3 pr-1.5 py-1.5">
                    <span className="text-sm text-white font-medium truncate">
                      {ownerLabel(selectedOwner)}
                    </span>
                    {selectedOwner.email && (
                      <span className="text-xs text-[#8a8f99] truncate">{selectedOwner.email}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedOwner(null);
                        setOwnerSearch('');
                      }}
                      className="w-6 h-6 flex items-center justify-center rounded-full text-[#B8B8C3] hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
                      title="Tulajdonos eltávolítása"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={ownerSearch}
                      onChange={(e) => setOwnerSearch(e.target.value)}
                      className="input-dark w-full"
                      placeholder="Tulajdonos keresése (név / email)…"
                    />
                    {ownerQuery.length >= 2 && (
                      <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-[#1d232a] border border-[#31383D] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {ownerMatches.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-[#8a8f99]">
                            {ownersLoaded ? 'Nincs találat.' : 'Felhasználók betöltése…'}
                          </p>
                        ) : (
                          ownerMatches.map((u) => (
                            <button
                              key={u.uid}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(u);
                                setOwnerSearch('');
                              }}
                              className="w-full text-left px-4 py-2.5 hover:bg-[#31383D] transition-colors"
                            >
                              <p className="text-sm text-white font-medium truncate">{ownerLabel(u)}</p>
                              {u.email && (
                                <p className="text-xs text-[#8a8f99] truncate">{u.email}</p>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-[#8a8f99] mt-1">
                  Ha kiválasztasz egy tulajdonost, ő éri el az appban a klub-nézetet (Dartiqon Area).
                  Tulajdonos nélkül a klub csak adminisztratív bejegyzésként jön létre.
                </p>
              </div>

              {createError && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 rounded-lg px-4 py-3 text-sm">
                  {createError}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={createClub} disabled={creating} className="btn-primary flex-1 disabled:opacity-50">
                {creating ? 'Mentés...' : 'Mentés'}
              </button>
              <button
                onClick={closeCreateModal}
                disabled={creating}
                className="btn-secondary flex-1 disabled:opacity-50"
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubManagement;
